/**
 * Live Session Event Emitter
 * 
 * This service provides event emission for live cooking sessions
 * that Chef can subscribe to for AI-powered features.
 */

class LiveSessionEventEmitter {
    constructor() {
        this.listeners = new Map();
        this.sessionContext = {
            sessionId: null,
            userId: null,
            recipeId: null,
            draftId: null,
            currentStep: 0,
            totalSteps: 0,
            phase: 'initializing'
        };
    }

    /**
     * Set the current session context
     */
    setSessionContext(context) {
        this.sessionContext = {
            ...this.sessionContext,
            ...context
        };
    }

    /**
     * Get current session context
     */
    getSessionContext() {
        return { ...this.sessionContext };
    }

    /**
     * Subscribe to live session events
     * @param {string} eventType - The event type to listen for
     * @param {function} callback - The callback function
     */
    subscribe(eventType, callback) {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, []);
        }
        this.listeners.get(eventType).push(callback);
    }

    /**
     * Unsubscribe from live session events
     * @param {string} eventType - The event type to stop listening for
     * @param {function} callback - The callback function to remove
     */
    unsubscribe(eventType, callback) {
        if (this.listeners.has(eventType)) {
            const callbacks = this.listeners.get(eventType);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Emit an event to all subscribers
     * @param {string} eventType - The event type
     * @param {object} data - The event data
     */
    emit(eventType, data = {}) {
        const event = {
            type: eventType,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionContext.sessionId,
            userId: this.sessionContext.userId,
            recipeId: this.sessionContext.recipeId,
            draftId: this.sessionContext.draftId,
            context: this.getSessionContext(),
            data
        };

        // Log the event for debugging
        // console.log(`🔔 Live Session Event: ${eventType}`, event);

        // Notify all subscribers
        if (this.listeners.has(eventType)) {
            const callbacks = this.listeners.get(eventType);
            callbacks.forEach((callback, index) => {
                try {
                    callback(event);
                } catch (error) {
                    console.error(`Error in event listener ${index + 1} for ${eventType}:`, error);
                }
            });
        }
    }

    /**
     * Emit session.start event
     */
    emitSessionStart(data = {}) {
        console.log('🔍 EMITTING session.start', {
            context: this.sessionContext,
            data: data,
            hasContext: !!this.sessionContext,
            totalDurationMinutes: this.sessionContext?.totalDurationMinutes
        });
        
        this.emit('session.start', {
            ...data,
            context: this.sessionContext,
            phase: 'active'
        });
    }

    /**
     * Emit phase.change event
     */
    emitPhaseChange(newPhase, data = {}) {
        const oldPhase = this.sessionContext.phase;
        this.sessionContext.phase = newPhase;
        
        this.emit('phase.change', {
            ...data,
            context: this.sessionContext,
            oldPhase,
            newPhase,
            phase: newPhase
        });
    }

    /**
     * Emit timer.tick event (fires when a minute passes or timer finishes)
     */
    emitTimerTick(timeRemaining, data = {}) {
        this.emit('timer.tick', {
            ...data,
            context: this.sessionContext,
            timeRemaining,
            isActive: timeRemaining > 0
        });
    }

    /**
     * Emit photo.added event
     */
    emitPhotoAdded(photoData, data = {}) {
        this.emit('photo.added', {
            ...data,
            photo: photoData,
            stepIndex: this.sessionContext.currentStep
        });
    }

    /**
     * Emit note.added event
     */
    emitNoteAdded(noteData, data = {}) {
        this.emit('note.added', {
            ...data,
            note: noteData,
            stepIndex: this.sessionContext.currentStep
        });
    }

    /**
     * Emit session.end event
     */
    emitSessionEnd(data = {}) {
        this.emit('session.end', {
            ...data,
            context: this.sessionContext,
            phase: 'completed',
            totalElapsedTime: data.totalElapsedTime || 0,
            wasCompleted: data.wasCompleted || false
        });
    }

    /**
     * Clear all listeners (useful for cleanup)
     */
    clearAllListeners() {
        this.listeners.clear();
    }

    /**
     * Get all registered event types
     */
    getEventTypes() {
        return Array.from(this.listeners.keys());
    }

    /**
     * Get listener count for an event type
     */
    getListenerCount(eventType) {
        return this.listeners.has(eventType) ? this.listeners.get(eventType).length : 0;
    }
}

// Create a singleton instance
const liveSessionEvents = new LiveSessionEventEmitter();

// Export both the class and the singleton instance
export { LiveSessionEventEmitter };
export default liveSessionEvents;
